/**
 * MessagingBadge - Secció de missatgeria a la fitxa de reserva
 *
 * Mostra l'estat dels missatges per cada kind:
 * - ✅ Enviat (amb data i usuari)
 * - ⏳ Pendent (amb botó per preparar)
 * - ❌ Sense WhatsApp
 */

import { useState } from 'react';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Separator } from '@/components/ui/separator';
import { toast } from 'sonner';
import {
  CheckCircle2,
  XCircle,
  MessageSquare,
  ChevronDown,
  ChevronUp,
} from 'lucide-react';
import { WhatsAppActionButton } from './WhatsAppActionButton';
import { useMessagingForReservation, useMarkNoWhatsapp } from '@/hooks/useMessagingTask';
import type { MessageKind, MessagingTask } from '@guilleumes/types';
import { cn } from '@/lib/utils';
import { hasAccessMessage } from '@/lib/messaging';
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from '@/components/ui/collapsible';

interface MessagingBadgeProps {
  reservationId: string;
  /** `hasWhatsapp` de la reserva: null = desconegut, false = marcat sense WhatsApp */
  hasWhatsapp?: boolean | null;
  /**
   * Propietat de Beds24 de la reserva. Si se sap i no té missatge de codi (D6: avui només
   * l'hotel), la fila Accés no surt: el backend el rebutjaria. `undefined` = no se sap, surt.
   */
  beds24PropertyId?: number | null;
}

// Emoji per kind
const kindEmoji: Record<MessageKind, string> = {
  FIRST_CONTACT: '📩',
  WELCOME: '👋',
  GUEST_FORM: '📋',
  ACCESS: '🔑',
  PAYMENT_LINK: '💳',
  REVIEW: '⭐',
  CUSTOM: '✏️',
};

// Label per kind
const kindLabel: Record<MessageKind, string> = {
  FIRST_CONTACT: 'Primer contacte',
  WELCOME: 'Benvinguda',
  GUEST_FORM: 'Registre',
  ACCESS: 'Accés',
  PAYMENT_LINK: 'Pagament',
  REVIEW: 'Ressenya',
  CUSTOM: 'Personalitzat',
};

// Prioritat d'ordre
const kindOrder: MessageKind[] = [
  'FIRST_CONTACT',
  'WELCOME',
  'GUEST_FORM',
  'ACCESS',
  'PAYMENT_LINK',
  'REVIEW',
  'CUSTOM',
];

function getTaskStatus(
  task: MessagingTask | undefined,
  hasWhatsapp: boolean | null | undefined,
): 'sent' | 'pending' | 'no-whatsapp' {
  if (hasWhatsapp === false) return 'no-whatsapp';
  if (!task) return 'pending';
  if (task.status === 'done') return 'sent';
  return 'pending';
}

function formatDate(dateStr?: string): string {
  if (!dateStr) return '';
  const date = new Date(dateStr);
  return date.toLocaleDateString('ca-ES', {
    day: 'numeric',
    month: 'short',
    hour: '2-digit',
    minute: '2-digit',
  });
}

export function MessagingBadge({
  reservationId,
  hasWhatsapp,
  beds24PropertyId,
}: MessagingBadgeProps) {
  const [isOpen, setIsOpen] = useState(true);
  const { latestByKind, config, isLoading } = useMessagingForReservation(reservationId);
  const markNoWhatsappMutation = useMarkNoWhatsapp();

  // Filtrar kinds segons enabledKinds de config
  const enabledKinds =
    config?.enabledKinds ??
    ({
      FIRST_CONTACT: true,
      WELCOME: true,
      GUEST_FORM: true,
      ACCESS: true,
      PAYMENT_LINK: false,
      REVIEW: false,
      CUSTOM: true,
    } as Record<MessageKind, boolean>);

  const accessHidden = beds24PropertyId !== undefined && !hasAccessMessage(beds24PropertyId);
  const visibleKinds = kindOrder.filter(
    (k) => enabledKinds[k] !== false && !(k === 'ACCESS' && accessHidden),
  );

  const handleNoWhatsapp = async (taskId?: string) => {
    if (!taskId) {
      toast.error('No hi ha tasca per marcar');
      return;
    }
    try {
      await markNoWhatsappMutation.mutateAsync(taskId);
      toast.success('Marcat com a sense WhatsApp');
    } catch {
      toast.error('Error marcant');
    }
  };

  if (isLoading) {
    return (
      <div className="py-4">
        <div className="flex items-center gap-2 text-muted-foreground">
          <div className="h-4 w-4 animate-spin rounded-full border-2 border-current border-t-transparent" />
          <span className="text-sm">Carregant missatgeria...</span>
        </div>
      </div>
    );
  }

  // Contar pendents
  const pendingCount = visibleKinds.filter(
    (k) => getTaskStatus(latestByKind[k], hasWhatsapp) === 'pending'
  ).length;

  return (
    <Collapsible open={isOpen} onOpenChange={setIsOpen}>
      <div className="bg-muted/50 rounded-lg p-4">
        <CollapsibleTrigger asChild>
          <div className="flex items-center justify-between cursor-pointer">
            <div className="flex items-center gap-2">
              <MessageSquare className="h-4 w-4 text-muted-foreground" />
              <h4 className="font-medium text-sm">📨 Missatgeria</h4>
              {pendingCount > 0 && (
                <Badge variant="secondary" className="text-xs">
                  {pendingCount} pendent{pendingCount > 1 ? 's' : ''}
                </Badge>
              )}
            </div>
            <Button variant="ghost" size="sm" className="h-6 w-6 p-0">
              {isOpen ? (
                <ChevronUp className="h-4 w-4" />
              ) : (
                <ChevronDown className="h-4 w-4" />
              )}
            </Button>
          </div>
        </CollapsibleTrigger>

        <CollapsibleContent>
          <Separator className="my-3" />

          <div className="space-y-2">
            {visibleKinds.map((kind) => {
              const task = latestByKind[kind];
              const status = getTaskStatus(task, hasWhatsapp);

              return (
                <div
                  key={kind}
                  className={cn(
                    'flex items-center justify-between py-2 px-3 rounded-md',
                    status === 'pending' && 'bg-yellow-50/50',
                    status === 'sent' && 'bg-green-50/50'
                  )}
                >
                  <div className="flex items-center gap-3">
                    <span className="text-lg">{kindEmoji[kind]}</span>
                    <div>
                      <p className="text-sm font-medium">{kindLabel[kind]}</p>
                      {status === 'sent' && task?.sentAt && (
                        <p className="text-xs text-muted-foreground">
                          ✅ Enviat {formatDate(task.sentAt)}
                          {task.sentByName && ` per ${task.sentByName}`}
                        </p>
                      )}
                      {status === 'pending' && !task && (
                        <p className="text-xs text-muted-foreground">No iniciat</p>
                      )}
                      {status === 'pending' && task && (
                        <p className="text-xs text-muted-foreground">
                          ⏳ Pendent
                          {task.dueDate &&
                            ` (venciment: ${new Date(task.dueDate).toLocaleDateString('ca-ES')})`}
                        </p>
                      )}
                    </div>
                  </div>

                  <div className="flex items-center gap-1">
                    {status === 'sent' ? (
                      <CheckCircle2 className="h-5 w-5 text-green-600" />
                    ) : status === 'no-whatsapp' ? (
                      <XCircle className="h-5 w-5 text-red-500" />
                    ) : (
                      <div className="flex items-center gap-1">
                        <WhatsAppActionButton
                          reservationId={reservationId}
                          messageKind={kind}
                          variant="ghost"
                          size="sm"
                          onSent={() => {
                            toast.success(`${kindLabel[kind]} marcat com enviat`);
                          }}
                        />
                        {task && (
                          <Button
                            variant="ghost"
                            size="sm"
                            className="h-7 w-7 p-0 text-muted-foreground hover:text-red-600"
                            onClick={() => handleNoWhatsapp(task.id)}
                            title="Marcar com a sense WhatsApp"
                          >
                            <XCircle className="h-4 w-4" />
                          </Button>
                        )}
                      </div>
                    )}
                  </div>
                </div>
              );
            })}

            {/* Botó nou missatge personalitzat */}
            <div className="pt-2">
              <WhatsAppActionButton
                reservationId={reservationId}
                messageKind="CUSTOM"
                variant="outline"
                size="sm"
                onSent={() => {
                  toast.success('Missatge personalitzat marcat com enviat');
                }}
              />
            </div>
          </div>
        </CollapsibleContent>
      </div>
    </Collapsible>
  );
}
